import React, { useEffect, useRef } from 'react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

export interface LightboxMedia {
    url: string;
    kind: 'image' | 'video';
    /** For a video: where to start playing, in seconds. */
    startAt?: number;
    /** Read by screen readers; not shown. */
    title?: string;
}

/**
 * A picture or a video, large, on its own — what clicking a thumbnail opens.
 * Videos play with their controls; closing stops them.
 */
export const MediaLightbox: React.FC<{ media: LightboxMedia | null; onClose: () => void }> = ({ media, onClose }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !media?.startAt) return undefined;
        const seek = () => { v.currentTime = media.startAt || 0; };
        if (v.readyState >= 1) seek(); else v.addEventListener('loadedmetadata', seek, { once: true });
        return () => v.removeEventListener('loadedmetadata', seek);
    }, [media]);

    return (
        <Dialog open={!!media} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-auto max-w-[min(92vw,1100px)] gap-0 overflow-hidden border-0 bg-transparent p-0 shadow-none sm:max-w-[min(92vw,1100px)]">
                <DialogTitle className="sr-only">{media?.title || 'Preview'}</DialogTitle>
                {media?.kind === 'video' ? (
                    <video ref={videoRef} src={media.url} controls autoPlay playsInline className="max-h-[85vh] max-w-full rounded-xl bg-black" />
                ) : media ? (
                    <img src={media.url} alt={media.title || ''} className="max-h-[85vh] max-w-full rounded-xl object-contain" />
                ) : null}
            </DialogContent>
        </Dialog>
    );
};
